import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { msg } from '@gonfalon/messages/msg';
import {
  account,
  aiDataset,
  alert,
  assignmentDataSource,
  attributeDataSource,
  destination,
  environment,
  environmentReleasePlan,
  flag,
  integration,
  ipAllowlist,
  member,
  metric,
  metricDataSource,
  metricGroup,
  notificationSubscription,
  productAnalyticsDashboard,
  project,
  releasePolicy,
  type ResourceSpecifier,
  toString as resourceSpecifierToString,
  role,
  sdkKey,
  segment,
  team,
  token,
  vega,
  view,
} from '@gonfalon/resource-specifiers/ast';
import { type ApplicationScopedResource } from '@gonfalon/resource-specifiers/isApplicationScopedResource';
import { type EnvironmentScopedResource } from '@gonfalon/resource-specifiers/isEnvironmentScopedResource';
import { type MemberScopedResource } from '@gonfalon/resource-specifiers/isMemberScopedResource';
import { type ProjectScopedResource } from '@gonfalon/resource-specifiers/isProjectScopedResource';
import { type TopLevelResource } from '@gonfalon/resource-specifiers/isTopLevelResource';
import { getAccessCheckQuery } from '@gonfalon/rest-api/queries/authorization';
import { useQueries } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import type { ResourceActions } from '../../actions';
import type { AccessCheck, Application, Environment, Member, Project } from '../../types';

type ActionProps<T extends ResourceSpecifier['type']> =
  | { action: ResourceActions<T>; actionsAND?: never; actionsOR?: never }
  | { actionsAND: Array<ResourceActions<T>>; action?: never; actionsOR?: never }
  | { actionsOR: Array<ResourceActions<T>>; action?: never; actionsAND?: never };

export type UseAccessCheckProps<T extends ResourceSpecifier['type']> = {
  /**
   * The type of resource to check access for.
   */
  resourceType: T;
  /**
   * The name of the resource to check access for. Defaults to '*'. Can use a specific string, such as a flag key.
   */
  resourceName?: string;
  /**
   * The tags of the resource to check access for. Defaults to [].
   */
  resourceTags?: string[];
  /**
   * Keys of the views the resource is linked to. Defaults to []. Only honored for
   * view-linkable resources (`flag`, `feature`, `segment`) — supplying them lets the
   * access-check API match a member whose access comes from a view-scoped role
   * (`proj/*:env/*:flag/*;view:X`). Other resource types ignore this field.
   */
  viewKeys?: string[];
  /**
   * The view's `_affectsSdkPayload` property. Only honored for `view` resources —
   * it rides in the resource specifier as the `{affectsSdkPayload:<bool>}` property
   * selector so role statements scoped by it (payload-filtering custom roles) match
   * the checked view. Omitting it makes such statements not match at all.
   */
  affectsSdkPayload?: boolean;
  /**
   * The project to check access for. Only required if the resourceType is a project-scoped resource.
   */
  project?: Project;
  /**
   * The environment to check access for. Only required if the resourceType is an environment-scoped resource.
   */
  environment?: Environment;
  /**
   * The member to check access for. Only required if the resourceType is a member-scoped resource.
   *
   * Only `_id` is read at runtime (see `getMember()` below), so the prop is
   * typed as `Pick<Member, '_id'>` rather than the full `Member` schema.
   * Callers that already have a full `Member` can pass it as-is; callers
   * that only have a member id (e.g. row-level access checks against a
   * resource owned by another member) can pass `{ _id: memberId }` directly
   * without reaching for an unsafe type assertion.
   */
  member?: Pick<Member, '_id'>;
  /**
   * The application to check access for. Only required if the resourceType is an application-scoped resource.
   */
  application?: Application;
} & ActionProps<T> &
  (
    | { resourceType: EnvironmentScopedResource['type']; project: Project; environment: Environment }
    | { resourceType: ProjectScopedResource['type']; project: Project }
    | { resourceType: MemberScopedResource['type']; member: Pick<Member, '_id'> }
    | { resourceType: ApplicationScopedResource['type']; application: Application }
    | { resourceType: TopLevelResource['type'] }
  );

export type UseAccessCheckResult = {
  /**
   * The access check result containing isAllowed, isLoading, and optional customMessage.
   */
  access: AccessCheck;
  /**
   * Whether the control should be disabled based on the access check.
   * True when loading, error, or access is denied.
   */
  isDisabled: boolean;
  /**
   * Whether the access check is still loading.
   */
  isLoading: boolean;
  /**
   * Any error that occurred during the access check.
   */
  error: Error | null;
  /**
   * The raw data from the access check API response.
   * This is useful for advanced use cases like checking for partial denials.
   */
  data: { allowed?: boolean; reason?: string; roleMaybeAllowsAccess?: { policy?: unknown } } | undefined;
};

/**
 * A headless hook for checking access permissions.
 *
 * This hook performs an access check via the API and returns the result without any UI wrapper.
 * Use this hook when you need access check results without the automatic Provider/tooltip/disabled
 * UI behavior, such as inside menus or when checking multiple permissions.
 *
 * For the common case of wrapping a button/link with automatic disabled state and tooltip,
 * use the `DynamicAccessCheck` component instead.
 *
 * @example
 * ```tsx
 * // Single action check
 * function MyMenu() {
 *   const { access, isDisabled } = useAccessCheck({
 *     resourceType: 'release-policy',
 *     action: 'updateReleasePolicy',
 *     resourceName: policy.key,
 *     project,
 *   });
 *
 *   return (
 *     <Menu>
 *       <MenuItem isDisabled={isDisabled}>
 *         {access.isAllowed ? 'Edit' : 'View'}
 *       </MenuItem>
 *     </Menu>
 *   );
 * }
 *
 * // Multiple actions (AND — all must be allowed)
 * const { isDisabled } = useAccessCheck({
 *   resourceType: 'flag',
 *   actionsAND: ['updateOn', 'updateTargets'],
 *   project,
 *   environment,
 * });
 *
 * // Multiple actions (OR — any being allowed is sufficient)
 * const { isDisabled } = useAccessCheck({
 *   resourceType: 'flag',
 *   actionsOR: ['updateOn', 'updateTargets'],
 *   project,
 *   environment,
 * });
 * ```
 */
export function useAccessCheck<T extends ResourceSpecifier['type']>({
  resourceName = '*',
  resourceTags = [],
  viewKeys = [],
  affectsSdkPayload,
  ...props
}: UseAccessCheckProps<T>): UseAccessCheckResult {
  function getProject() {
    invariant(props.project);
    // Coerce nullable tags to [] — the OpenAPI type claims `string[]` but the
    // API occasionally returns `null`, which crashes downstream `.length` reads.
    return project(props.project.key, props.project.tags ?? []);
  }

  function getEnvironment() {
    invariant(props.environment);
    return environment(getProject(), props.environment.key, props.environment.tags ?? [], props.environment.critical);
  }

  function getMember() {
    invariant(props.member);
    return member(props.member._id);
  }

  function getResource() {
    switch (props.resourceType) {
      case 'env':
        return environment(getProject(), resourceName, resourceTags);
      case 'flag':
      case 'feature':
        return flag(getEnvironment(), resourceName, resourceTags, viewKeys);
      case 'proj':
        return project(resourceName, resourceTags);
      case 'product-analytics-dashboard':
        return productAnalyticsDashboard(getEnvironment(), resourceName, resourceTags);
      case 'role':
        return role(resourceName, resourceTags);
      case 'ai-dataset':
        return aiDataset(getProject(), resourceName, resourceTags);
      case 'segment':
        return segment(getEnvironment(), resourceName, resourceTags, viewKeys);
      case 'destination':
        return destination(getEnvironment(), resourceName, resourceTags);
      case 'assignment-data-source':
        return assignmentDataSource(getEnvironment(), resourceName, resourceTags);
      case 'attribute-data-source':
        return attributeDataSource(getEnvironment(), resourceName, resourceTags);
      case 'notification-subscription':
        return notificationSubscription(getEnvironment(), resourceName, resourceTags);
      case 'view':
        return view(getProject(), resourceName, resourceTags, undefined, undefined, affectsSdkPayload);
      case 'metric':
        return metric(getProject(), resourceName, resourceTags);
      case 'metric-data-source':
        return metricDataSource(getEnvironment(), resourceName, resourceTags);
      case 'metric-group':
        return metricGroup(getProject(), resourceName, resourceTags);
      case 'release-policy':
        return releasePolicy(getProject(), resourceName, resourceTags);
      case 'environment-release-plan':
        return environmentReleasePlan(getEnvironment(), resourceName, resourceTags);
      case 'team':
        return team(resourceName);
      case 'member':
        return getMember();
      case 'sdk-key':
        return sdkKey(getEnvironment(), resourceName, resourceTags);
      case 'token':
        return token(getMember(), resourceName, resourceTags);
      case 'vega':
        return vega(getProject(), resourceName, resourceTags);
      case 'acct':
        return account();
      case 'alert':
        return alert(getProject(), resourceName, resourceTags);
      case 'integration':
        return integration(resourceName, resourceTags);
      case 'ip-allowlist':
        return ipAllowlist();
      default:
        throw new Error(`Unsupported resource type: ${props.resourceType}`);
    }
  }

  const resourceString = resourceSpecifierToString(getResource());
  const actionsArray = props.actionsAND ?? props.actionsOR ?? [props.action];
  const mode: 'and' | 'or' = props.actionsOR ? 'or' : 'and';

  const results = useQueries({
    queries: actionsArray.map((action) => ({
      ...getAccessCheckQuery({
        resource: resourceString,
        action,
      }),
      staleTime: 5 * 60 * 1000, // 5 minutes - access checks don't change frequently
    })),
  });

  const isLoading = results.some((r) => r.isPending);
  const error = results.find((r) => r.error)?.error ?? null;

  const firstDeniedResult = results.find((r) => !r.isPending && !isAllowedResponse(r.data));
  const data = firstDeniedResult?.data ?? results[0]?.data;

  let isAllowed = false;
  if (mode === 'and' && props.actionsAND) {
    isAllowed = results.every((r) => isAllowedResponse(r.data));
  } else if (mode === 'or' && props.actionsOR) {
    isAllowed = results.some((r) => isAllowedResponse(r.data));
  } else if (props.action) {
    isAllowed = isAllowedResponse(data);
  } else {
    throw new Error('No action provided');
  }

  const access: AccessCheck = isAllowed
    ? { isAllowed: true, isLoading }
    : {
        isAllowed: false,
        isLoading,
        customMessage: (() => {
          if (isLoading) {
            return;
          }

          // Any error — REST or not (e.g. a network failure) — must read as a
          // failed check, never as a permission denial the member may not have.
          if (error) {
            return (isRESTAPIError(error) && error.message) || msg('permissions.restrictedControl.checkError');
          }

          if (typeof data?.reason === 'string' && data.reason.length > 0) {
            return data.reason;
          }

          return msg('permissions.restrictedControl.denied');
        })(),
      };

  const isDisabled = (() => {
    // If we're still loading or there's an error, disable controls for security
    if (isLoading || error) {
      return true;
    }

    // Only enable if we have data and access is explicitly allowed
    return !access.isAllowed;
  })();

  return {
    access,
    isDisabled,
    isLoading,
    error: error instanceof Error ? error : null,
    data,
  };
}

function isAllowedResponse(data: unknown): boolean {
  return !!data && typeof data === 'object' && 'allowed' in data && !!(data as { allowed?: boolean }).allowed;
}
